import { worldCities } from '@/data/cities';
import { specialties } from '@/data/specialties';
import { cityLanguageNames, CityLanguage } from '@/context/languages/languageData';
import { detectCityLanguage } from '@/utils/cityLanguageDetector';

export interface MultilingualRoute {
  path: string;
  language: string;
  priority: number;
  type: 'main' | 'city' | 'service';
  alternates?: Array<{ href: string; hreflang: string }>;
}

/**
 * Generates all multilingual routes for the application
 */
export const generateMultilingualRoutes = (): MultilingualRoute[] => {
  const routes: MultilingualRoute[] = [];
  const baseUrl = 'https://psychotherapist.help';
  
  // Create supported languages array from cityLanguageNames
  const supportedLanguages = Object.keys(cityLanguageNames).map(code => ({
    code: code as CityLanguage,
    name: cityLanguageNames[code as CityLanguage]
  }));

  // Main pages routes
  const mainPages = [
    { path: '', priority: 1.0 },
    { path: '/about', priority: 0.8 },
    { path: '/contact', priority: 0.7 },
    { path: '/find', priority: 0.9 },
    { path: '/how-it-works', priority: 0.8 },
    { path: '/pricing', priority: 0.8 },
    { path: '/specialties', priority: 0.8 },
    { path: '/for-therapists', priority: 0.8 },
    { path: '/jose-luis-marin', priority: 0.7 },
    { path: '/online-therapy', priority: 0.9 },
    { path: '/resources', priority: 0.7 }
  ];

  // Add main pages for each language
  supportedLanguages.forEach(lang => {
    mainPages.forEach(page => {
      const englishPath = page.path;
      const localizedPath = lang.code === 'en' ? page.path : `/${lang.code}${page.path}`;
      
      routes.push({
        path: localizedPath,
        language: lang.code,
        priority: lang.code === 'en' ? page.priority : page.priority * 0.9,
        type: 'main',
        alternates: supportedLanguages.map(altLang => ({
          href: `${baseUrl}${altLang.code === 'en' ? englishPath : `/${altLang.code}${englishPath}`}`,
          hreflang: altLang.code
        }))
      });
    });
  });

  // City routes
  worldCities.forEach(city => {
    const cityLanguage = detectCityLanguage(city);
    
    // English city page
    routes.push({
      path: `/city/${city.slug}`,
      language: 'en',
      priority: 0.9,
      type: 'city',
      alternates: [
        { href: `${baseUrl}/city/${city.slug}`, hreflang: 'en' },
        ...(cityLanguage !== 'en' ? [{
          href: `${baseUrl}/${cityLanguage}/city/${city.slug}`,
          hreflang: cityLanguage
        }] : [])
      ]
    });

    // Local language city page (if different from English)
    if (cityLanguage !== 'en') {
      routes.push({
        path: `/${cityLanguage}/city/${city.slug}`,
        language: cityLanguage,
        priority: 0.95, // Higher priority for local language
        type: 'city'
      });
    }

    // Service-specific city pages
    specialties.forEach(service => {
      // English service page
      routes.push({
        path: `/city/${city.slug}/${service.slug}`,
        language: 'en',
        priority: 0.8,
        type: 'service',
        alternates: [
          { href: `${baseUrl}/city/${city.slug}/${service.slug}`, hreflang: 'en' },
          ...(cityLanguage !== 'en' ? [{
            href: `${baseUrl}/${cityLanguage}/city/${city.slug}/${service.slug}`,
            hreflang: cityLanguage
          }] : [])
        ]
      });

      // Local language service page
      if (cityLanguage !== 'en') {
        routes.push({
          path: `/${cityLanguage}/city/${city.slug}/${service.slug}`,
          language: cityLanguage,
          priority: 0.85, // Higher priority for local language
          type: 'service'
        });
      }
    });
  });

  return routes;
};

/**
 * Get routes by language
 */
export const getRoutesByLanguage = (language: string): MultilingualRoute[] => {
  return generateMultilingualRoutes().filter(route => route.language === language);
};

/**
 * Get route statistics
 */
export const getRouteStatistics = () => {
  const routes = generateMultilingualRoutes();
  const supportedLanguages = Object.keys(cityLanguageNames);
  
  const stats = {
    total: routes.length,
    byLanguage: {} as Record<string, number>,
    byType: {} as Record<string, number>,
    cities: worldCities.length,
    services: specialties.length,
    languages: supportedLanguages.length
  };

  routes.forEach(route => {
    stats.byLanguage[route.language] = (stats.byLanguage[route.language] || 0) + 1;
    stats.byType[route.type] = (stats.byType[route.type] || 0) + 1;
  });

  return stats;
};

/**
 * Generate robots.txt content
 */
export const generateRobotsTxt = (): string => {
  const baseUrl = 'https://psychotherapist.help';
  const supportedLanguages = Object.keys(cityLanguageNames);
  
  return `User-agent: *
Allow: /

# Sitemaps
Sitemap: ${baseUrl}/sitemap.xml
Sitemap: ${baseUrl}/sitemap-images.xml
Sitemap: ${baseUrl}/sitemap-news.xml

# Language-specific sitemaps
${supportedLanguages.map(lang => 
  `Sitemap: ${baseUrl}/sitemap-${lang}.xml`
).join('\n')}

# Block admin and development paths
Disallow: /admin/
Disallow: /dev/
Disallow: /api/internal/
Disallow: /_next/
Disallow: /private/

# Allow all other content
Allow: /city/
Allow: /country/
Allow: /specialties/
Allow: /therapist/

# Crawl delay (optional - can be adjusted)
Crawl-delay: 1`;
};

/**
 * Generate sitemap index
 */
export const generateSitemapIndex = (): string => {
  const baseUrl = 'https://psychotherapist.help';
  const currentDate = new Date().toISOString();
  const supportedLanguages = Object.keys(cityLanguageNames);
  
  let xml = `<?xml version="1.0" encoding="UTF-8"?>\n`;
  xml += `<sitemapindex xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n`;
  
  // Main sitemap
  xml += `  <sitemap>\n`;
  xml += `    <loc>${baseUrl}/sitemap.xml</loc>\n`;
  xml += `    <lastmod>${currentDate}</lastmod>\n`;
  xml += `  </sitemap>\n`;
  
  // Language-specific sitemaps
  supportedLanguages.forEach(lang => {
    xml += `  <sitemap>\n`;
    xml += `    <loc>${baseUrl}/sitemap-${lang}.xml</loc>\n`;
    xml += `    <lastmod>${currentDate}</lastmod>\n`;
    xml += `  </sitemap>\n`;
  });
  
  // Specialized sitemaps
  const specializedSitemaps = ['images', 'news', 'cities', 'services'];
  specializedSitemaps.forEach(type => {
    xml += `  <sitemap>\n`;
    xml += `    <loc>${baseUrl}/sitemap-${type}.xml</loc>\n`;
    xml += `    <lastmod>${currentDate}</lastmod>\n`;
    xml += `  </sitemap>\n`;
  });
  
  xml += `</sitemapindex>`;
  return xml;
};